// src/pages/AddCourse.jsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createCourse } from "../firebase/database";

export default function AddCourse() {
  const [formData, setFormData] = useState({
    name: "",
    totalLessons: "",
    totalDurationHours: "",
    classHours: "",
    practiceHours: "",
    fees: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Course name is required");
      return;
    }

    setLoading(true);
    try {
      await createCourse(formData);
      navigate("/admin/courses");
    } catch (err) {
      console.error("Error creating course:", err);
      setError("Failed to create course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <Link 
            to="/admin/courses" 
            style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            Add New Course
          </h1>
        </div>
        <p style={{ color: "#6B7280", margin: "4px 0 0 36px", fontFamily: "DM Sans, sans-serif" }}>
          Create a new course with its details
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        padding: 24 
      }}>
        {error && (
          <div style={{ 
            background: "#FEE2E2", 
            color: "#DC2626", 
            padding: "12px 16px", 
            borderRadius: 8, 
            marginBottom: 20,
            fontFamily: "DM Sans, sans-serif"
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Course Name */}
          <div>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Course Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Python Programming"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "DM Sans, sans-serif",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
            />
          </div>

          {/* Number of Lessons */}
          <div>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Number of Lessons
            </label>
            <input
              type="number"
              name="totalLessons"
              value={formData.totalLessons}
              onChange={handleChange}
              placeholder="e.g., 30"
              min="0"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "DM Sans, sans-serif",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
            />
          </div>

          {/* Duration & Hours Row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {/* Total Duration */}
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Total Duration (hrs)
              </label>
              <input
                type="number"
                name="totalDurationHours"
                value={formData.totalDurationHours}
                onChange={handleChange}
                placeholder="e.g., 60"
                min="0"
                step="0.5"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
                onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
              />
            </div>

            {/* Class Hours */}
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Class Hours
              </label>
              <input
                type="number"
                name="classHours"
                value={formData.classHours}
                onChange={handleChange}
                placeholder="e.g., 30"
                min="0"
                step="0.5"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
                onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
              />
            </div>

            {/* Practice Hours */}
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Practice Hours
              </label>
              <input
                type="number"
                name="practiceHours"
                value={formData.practiceHours}
                onChange={handleChange}
                placeholder="e.g., 30"
                min="0"
                step="0.5"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
                onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
              />
            </div>
          </div>

          {/* Fees */}
          <div>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Fees (₹)
            </label>
            <input
              type="number"
              name="fees"
              value={formData.fees}
              onChange={handleChange}
              placeholder="e.g., 5000"
              min="0"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "DM Sans, sans-serif",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
            />
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              padding: "14px 24px",
              background: loading ? "#9CA3AF" : "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "background 0.2s",
            }}
          >
            {loading ? "Creating..." : "Submit"}
          </button>
          <Link
            to="/admin/courses"
            style={{
              padding: "14px 24px",
              background: "white",
              color: "#6B7280",
              border: "1px solid #D1D5DB",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}